import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Flame, ChevronDown, ChevronUp, Heart, MessageCircle, TrendingUp, Share2, Send, Zap, Users, Target, ArrowUpRight, Instagram, BarChart3 } from 'lucide-react';
import { cn } from '../../lib/utils';
import TiktokIcon from '../../components/ui/TiktokIcon';

// Helper function to interpolate between two colors based on temperature
const interpolateColor = (temperature) => {
  // Define base colors
  const notViralColor = '#3B82F6'; // Cool blue
  const viralColor = '#EF4444';    // Warm red
  
  // If temperature is undefined/null (unposted video), return the blue color
  if (temperature === undefined || temperature === null) {
    return notViralColor;
  }
  
  // Ensure temperature is between 0 and 1
  const t = Math.max(0, Math.min(1, temperature));
  
  // Convert hex to RGB
  const fromRGB = {
    r: parseInt(notViralColor.slice(1, 3), 16),
    g: parseInt(notViralColor.slice(3, 5), 16),
    b: parseInt(notViralColor.slice(5, 7), 16)
  };
  
  const toRGB = {
    r: parseInt(viralColor.slice(1, 3), 16),
    g: parseInt(viralColor.slice(3, 5), 16),
    b: parseInt(viralColor.slice(5, 7), 16)
  };
  
  // Interpolate between colors
  const resultRGB = {
    r: Math.round(fromRGB.r + (toRGB.r - fromRGB.r) * t),
    g: Math.round(fromRGB.g + (toRGB.g - fromRGB.g) * t),
    b: Math.round(fromRGB.b + (toRGB.b - fromRGB.b) * t)
  };
  
  // Convert back to hex
  return `#${resultRGB.r.toString(16).padStart(2, '0')}${resultRGB.g.toString(16).padStart(2, '0')}${resultRGB.b.toString(16).padStart(2, '0')}`;
};

// Add keyframes for the red pulse animation
const redPulseAnimation = `
  @keyframes redPulse {
    0% {
      filter: drop-shadow(0 0 0px rgba(239, 68, 68, 0));
    }
    50% {
      filter: drop-shadow(0 0 2px rgba(239, 68, 68, 0.6));
    }
    100% {
      filter: drop-shadow(0 0 0px rgba(239, 68, 68, 0));
    }
  }
`;

// Helper function to format numbers with K, M, B notation
const formatNumber = (num) => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

const VideoItem = ({ title, colors, temperature, postId, comments = [], stats, strategy }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [likedComments, setLikedComments] = useState(new Set());
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [replyTexts, setReplyTexts] = useState({});
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [completedTasks, setCompletedTasks] = useState(new Set());

  // Calculate all tasks for this video
  const videoTasks = useMemo(() => {
    const tasks = [];
    
    // Add comment-based tasks
    comments.forEach((comment, index) => {
      if (comment.likes > 100 || comment.isInfluencer) {
        tasks.push({
          id: `comment-${index}`,
          type: 'comment',
          priority: comment.likes > 500 ? 'high' : 'medium',
          title: 'Engage with High-Value Comment',
          description: `Reply to ${comment.author}'s comment`,
          content: comment.comment,
          metadata: {
            author: comment.author,
            likes: comment.likes,
            timestamp: comment.timestamp,
            commentIndex: index
          },
          isCompleted: completedTasks.has(`comment-${index}`)
        });
      }
    });

    // Add engagement-based tasks
    if (stats) {
      if (stats.likes > 1000 && stats.comments < stats.likes * 0.1) {
        tasks.push({
          id: 'boost-engagement',
          type: 'engagement',
          priority: 'high',
          title: 'Boost Engagement Rate',
          description: 'Add a comment to encourage more discussion',
          content: 'Engagement rate is lower than average. Add a comment to spark discussion.',
          metadata: {
            currentRate: `${((stats.comments / stats.likes) * 100).toFixed(1)}%`,
            targetRate: '10%'
          },
          isCompleted: completedTasks.has('boost-engagement')
        });
      }
    }

    // Add viral-related tasks
    if (temperature >= 0.8) {
      tasks.push({
        id: 'viral-analysis',
        type: 'analysis',
        priority: 'high',
        title: 'Analyze Viral Success',
        description: 'Document what made this content successful',
        content: 'This content is performing exceptionally well. Analyze and document the key factors.',
        metadata: {
          temperature: temperature,
          stats: stats
        },
        isCompleted: completedTasks.has('viral-analysis')
      });
    }

    // Sort tasks by priority and completion status
    return tasks.sort((a, b) => {
      if (a.isCompleted !== b.isCompleted) return a.isCompleted - b.isCompleted;
      if (a.priority === 'high' && b.priority !== 'high') return -1;
      if (b.priority === 'high' && a.priority !== 'high') return 1;
      return 0;
    });
  }, [comments, stats, temperature, completedTasks]);

  const handleTaskClick = (e, taskId) => {
    e.stopPropagation();
    setExpandedTasks(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(taskId)) {
        newExpanded.delete(taskId);
      } else {
        newExpanded.add(taskId);
      }
      return newExpanded;
    });
  };

  const handleTaskComplete = (taskId, data = {}) => {
    // Handle task completion based on type
    const task = videoTasks.find(t => t.id === taskId);
    if (task) {
      if (task.type === 'comment' && !data.reply?.trim()) return;
      
      // Mark task as completed
      setCompletedTasks(prev => new Set([...prev, taskId]));
      
      // Clear any associated input
      if (task.type === 'comment') {
        setReplyTexts(prev => ({
          ...prev,
          [task.metadata.commentIndex]: ''
        }));
      }
      
      // Collapse the task
      setExpandedTasks(prev => {
        const newExpanded = new Set(prev);
        newExpanded.delete(taskId);
        return newExpanded;
      });
    }
  };

  useEffect(() => {
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const targetPostId = urlParams.get('post_id');
    const targetCommentId = urlParams.get('comment_id');

    // Only proceed if this is the target post
    if (targetPostId === postId) {
      setIsExpanded(true);
      
      // If there's a comment_id, expand that comment
      if (targetCommentId) {
        const commentIndex = parseInt(targetCommentId, 10);
        // Validate the comment index exists
        if (!isNaN(commentIndex) && commentIndex >= 0 && commentIndex < comments.length) {
          setExpandedTasks(new Set([`comment-${commentIndex}`]));
          // Scroll into view after a short delay to ensure elements are rendered
          setTimeout(() => {
            const commentElement = document.getElementById(`comment-${postId}-${commentIndex}`);
            if (commentElement) {
              commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      }
    }
  }, [postId, comments.length]);

  const handleBiggestFansClick = async (e) => {
    e.stopPropagation(); // Prevent expanding/collapsing when clicking the link
    const url = `${window.location.origin}/oplaes?post_id=${postId}`;
    
    try {
      await navigator.clipboard.writeText(url);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Get temperature-based color with 0.2 opacity for background
  const temperatureColor = interpolateColor(temperature);
  const backgroundStyle = temperature !== undefined && temperature !== null
    ? { backgroundColor: `${temperatureColor}20` } // 20% opacity for more visible tint
    : { backgroundColor: '#3B82F620' }; // 20% opacity blue tint for unposted
  
  const isViral = temperature >= 0.8;
  
  const hasExpandableContent = comments.length > 0 || stats || strategy;

  const handleLike = (e, commentIndex) => {
    e.stopPropagation(); // Prevent expanding/collapsing when clicking like
    setLikedComments(prev => {
      const newLiked = new Set(prev);
      if (newLiked.has(commentIndex)) {
        newLiked.delete(commentIndex);
      } else {
        newLiked.add(commentIndex);
      }
      return newLiked;
    });
  };

  const handleCommentClick = (e, index) => {
    e.stopPropagation();
    setExpandedTasks(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  };

  const handleReplyChange = (index, text) => {
    setReplyTexts(prev => ({
      ...prev,
      [index]: text
    }));
  };

  const handleShareComment = async (e, commentIndex) => {
    e.stopPropagation();
    const url = `${window.location.origin}/oplaes?post_id=${postId}&comment_id=${commentIndex}`;
    
    try {
      await navigator.clipboard.writeText(url);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Calculate flow insights
  const flowInsights = useMemo(() => {
    const insights = [];
    let contentVelocity = null;
    let engagementTrend = null;
    
    // Always show task completion if there are any tasks
    if (videoTasks.length > 0) {
      insights.push({ 
        label: 'Tasks', 
        value: `${completedTasks.size}/${videoTasks.length}`, 
        color: completedTasks.size === videoTasks.length ? 'text-green-400' : 'text-blue-400',
        icon: <Zap className="w-3 h-3" />
      });
    }

    if (stats) {
      contentVelocity = stats.likes > 1000 ? '+22% WoW' : null;
      engagementTrend = stats.comments / stats.likes > 0.1 ? '+1.4x Avg' : null;

      if (contentVelocity) insights.push({ 
        label: 'Content Velocity', 
        value: contentVelocity, 
        color: 'text-green-400',
        icon: <TrendingUp className="w-3 h-3" />
      });
      if (engagementTrend) insights.push({ 
        label: 'Engagement Trend', 
        value: engagementTrend, 
        color: 'text-purple-400',
        icon: <Users className="w-3 h-3" />
      });
    }

    return insights;
  }, [stats, completedTasks.size, videoTasks.length]);

  return (
    <>
      <style>{redPulseAnimation}</style>
      {showCopiedToast && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg text-sm animate-fade-in-up">
          Share link copied to clipboard!
        </div>
      )}
      <div className="flex flex-col">
        {/* Flow Insights Bar */}
        {flowInsights.length > 0 && (
          <div className="flex items-center gap-4 px-4 py-2 bg-gray-800/30 rounded-t-2xl border-t border-l border-r border-gray-800">
            {flowInsights.map((insight, index) => (
              <React.Fragment key={insight.label}>
                <div className="flex items-center gap-2">
                  {insight.icon && (
                    <div className={cn("shrink-0", insight.color)}>
                      {insight.icon}
                    </div>
                  )}
                  <span className="text-xs text-gray-400">{insight.label}</span>
                  <span className={cn("text-xs font-medium tabular-nums", insight.color)}>{insight.value}</span>
                </div>
                {index < flowInsights.length - 1 && (
                  <div className="h-3 w-px bg-gray-700" />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
        
        {/* Main Content */}
        <div 
          className={cn(
            "flex items-center p-4 border border-gray-800",
            "bg-gradient-to-br from-gray-900/90 to-gray-800/90",
            flowInsights.length > 0 ? "rounded-b-2xl border-t-0" : "rounded-2xl",
            hasExpandableContent && "hover:border-gray-700 cursor-pointer"
          )}
          onClick={() => hasExpandableContent && setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center flex-1">
            {isViral && (
              <Flame 
                className="w-4 h-4 text-orange-500 mr-2 shrink-0" 
                style={{ animation: 'redPulse 2s ease-in-out infinite' }}
              />
            )}
            <div className="flex -space-x-1 mr-2 shrink-0">
              {Array.isArray(colors) ? colors.map((color, index) => (
                <div
                  key={index}
                  className="w-2.5 h-2.5 rounded-full ring-1 ring-gray-800"
                  style={{ backgroundColor: color, zIndex: colors.length - index }}
                />
              )) : (
                <div
                  className="w-2.5 h-2.5 rounded-full ring-1 ring-gray-800"
                  style={{ backgroundColor: colors }}
                />
              )}
            </div>
            <p className="text-sm text-gray-300 truncate">{title}</p>
          </div>
          
          {hasExpandableContent && (
            <ChevronDown 
              className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          )}
        </div>
        
        {isExpanded && hasExpandableContent && (
          <div className="border-l border-r border-b border-gray-800 bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-b-2xl overflow-hidden">
            {/* Stats Section */}
            {stats && (
              <div className="px-4 py-3 border-b border-gray-800">
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <MessageCircle className="w-4 h-4 text-blue-400 mr-1" />
                      <span className="text-lg font-semibold text-gray-300">{formatNumber(stats.comments)}</span>
                    </div>
                    <span className="text-xs text-gray-400">Comments</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <Share2 className="w-4 h-4 text-green-400 mr-1" />
                      <span className="text-lg font-semibold text-gray-300">{formatNumber(stats.shares)}</span>
                    </div>
                    <span className="text-xs text-gray-400">Shares</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      <Heart className="w-4 h-4 text-red-400 mr-1" />
                      <span className="text-lg font-semibold text-gray-300">{formatNumber(stats.likes)}</span>
                    </div>
                    <span className="text-xs text-gray-400">Likes</span>
                  </div>
                </div>
              </div>
            )}

            {/* Tasks Section */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <Zap className="w-4 h-4 text-blue-400 mr-2" />
                  <h3 className="text-sm font-medium text-gray-300">Tasks</h3>
                </div>
                {videoTasks.some(t => !t.isCompleted) && (
                  <div className="text-xs text-gray-400">
                    {videoTasks.filter(t => !t.isCompleted).length} pending tasks
                  </div>
                )}
              </div>
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 -mr-2">
                {videoTasks.map((task) => (
                  <div 
                    key={task.id}
                    className={cn(
                      "flex flex-col space-y-2 p-3 rounded-xl transition-all cursor-pointer",
                      "bg-gradient-to-br",
                      !task.isCompleted
                        ? task.priority === 'high'
                          ? "from-red-900/20 to-pink-900/20 border-red-500/30"
                          : "from-indigo-900/20 to-purple-900/20 border-indigo-500/30"
                        : "from-gray-800/50 to-gray-800/30 border-gray-700/50",
                      "hover:from-gray-800/70 hover:to-gray-800/50",
                      "border hover:border-gray-700",
                      task.isCompleted && "opacity-75"
                    )}
                    onClick={(e) => handleTaskClick(e, task.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-200">{task.title}</h4>
                          {!task.isCompleted && (
                            <div className={cn(
                              "px-1.5 py-0.5 rounded-full text-xs font-medium",
                              task.priority === 'high' 
                                ? "bg-red-500/20 text-red-400"
                                : "bg-yellow-500/20 text-yellow-400"
                            )}>
                              {task.priority === 'high' ? 'High Priority' : 'Medium Priority'}
                            </div>
                          )}
                          {task.isCompleted && (
                            <div className="bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full text-xs font-medium">
                              Completed
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{task.description}</p>
                        
                        {/* Task Type Specific Metadata */}
                        {task.type === 'comment' && (
                          <div className="flex items-center mt-2 space-x-4">
                            <div className="flex items-center space-x-1.5">
                              <Heart className="w-4 h-4 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {formatNumber(task.metadata.likes)}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">{task.metadata.timestamp}</span>
                          </div>
                        )}
                        {task.type === 'engagement' && (
                          <div className="flex items-center mt-2 space-x-2">
                            <span className="text-xs text-gray-500">Current: {task.metadata.currentRate}</span>
                            <span className="text-xs text-gray-500">→</span>
                            <span className="text-xs text-gray-500">Target: {task.metadata.targetRate}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Task Content */}
                    {expandedTasks.has(task.id) && !task.isCompleted && (
                      <div 
                        className="pl-3 border-l-2 border-gray-700 mt-2"
                        onClick={e => e.stopPropagation()}
                      >
                        {task.type === 'comment' && (
                          <>
                            <p className="text-sm text-gray-400 mb-3">{task.content}</p>
                            <textarea
                              className={cn(
                                "w-full p-3 text-sm rounded-xl resize-none",
                                "bg-gray-800/50 border border-gray-700",
                                "focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50",
                                "text-gray-200 placeholder-gray-500"
                              )}
                              placeholder="Write your reply..."
                              rows={2}
                              value={replyTexts[task.metadata.commentIndex] || ''}
                              onChange={(e) => handleReplyChange(task.metadata.commentIndex, e.target.value)}
                            />
                          </>
                        )}
                        {task.type === 'engagement' && (
                          <textarea
                            className={cn(
                              "w-full p-3 text-sm rounded-xl resize-none",
                              "bg-gray-800/50 border border-gray-700",
                              "focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50",
                              "text-gray-200 placeholder-gray-500"
                            )}
                            placeholder="Write an engaging comment..."
                            rows={2}
                            value={replyTexts[task.id] || ''}
                            onChange={(e) => handleReplyChange(task.id, e.target.value)}
                          />
                        )}
                        {task.type === 'analysis' && (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-400">{task.content}</p>
                            <textarea
                              className={cn(
                                "w-full p-3 text-sm rounded-xl resize-none",
                                "bg-gray-800/50 border border-gray-700",
                                "focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50",
                                "text-gray-200 placeholder-gray-500"
                              )}
                              placeholder="Document your analysis..."
                              rows={4}
                              value={replyTexts[task.id] || ''}
                              onChange={(e) => handleReplyChange(task.id, e.target.value)}
                            />
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center mt-2">
                          <button
                            className={cn(
                              "inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg",
                              "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors",
                              replyTexts[task.type === 'comment' ? task.metadata.commentIndex : task.id]?.trim()
                                ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 focus:ring-purple-500"
                                : "bg-gray-800 text-gray-400 cursor-not-allowed"
                            )}
                            onClick={() => handleTaskComplete(task.id, {
                              reply: replyTexts[task.type === 'comment' ? task.metadata.commentIndex : task.id]
                            })}
                            disabled={!replyTexts[task.type === 'comment' ? task.metadata.commentIndex : task.id]?.trim()}
                          >
                            <Zap className="w-4 h-4 mr-1.5" />
                            Complete Task
                          </button>
                          {task.type === 'comment' && (
                            <button
                              className="inline-flex items-center px-2 py-1.5 text-gray-400 hover:text-gray-300 focus:outline-none transition-colors"
                              onClick={(e) => handleShareComment(e, task.metadata.commentIndex)}
                            >
                              <Share2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const DateSeparator = ({ date }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'pm' : 'am';
    const formattedHours = hours % 12 || 12;
    
    return `${months[date.getMonth()]} ${date.getDate()} · ${days[date.getDay()]} ${formattedHours}${period}`;
  };

  return (
    <div className="flex items-center py-2">
      <div className="flex items-center text-xs text-gray-500 whitespace-nowrap">
        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        {formatDate(date)}
      </div>
      <div className="flex-1 ml-2 border-t border-gray-200"></div>
    </div>
  );
};

const VideoList = ({ videos }) => {
  // Sort videos by date, newest first
  const sortedVideos = [...videos].sort((a, b) => {
    if (!a.scheduled_date) return 1;
    if (!b.scheduled_date) return -1;
    return new Date(b.scheduled_date) - new Date(a.scheduled_date);
  });

  // Group videos by date
  const videosByDate = sortedVideos.reduce((acc, video) => {
    if (!video.scheduled_date) {
      if (!acc.unscheduled) acc.unscheduled = [];
      acc.unscheduled.push(video);
      return acc;
    }

    const date = new Date(video.scheduled_date);
    const dateKey = date.toISOString().split('T')[0];
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(video);
    return acc;
  }, {});

  return (
    <div className="space-y-1">
      {/* Show unscheduled videos first */}
      {videosByDate.unscheduled && videosByDate.unscheduled.map((video, videoIndex) => (
        <VideoItem 
          key={`unscheduled-${videoIndex}`}
          title={video.title}
          colors={video.colors || video.color}
          temperature={video.temperature}
          postId={video.id}
          comments={video.comments || []}
          stats={video.stats}
          strategy={video.strategy}
        />
      ))}

      {/* Then show scheduled videos grouped by date */}
      {Object.entries(videosByDate)
        .filter(([dateKey]) => dateKey !== 'unscheduled')
        .map(([dateKey, dateVideos], index) => (
          <div key={dateKey} className="space-y-1">
            <DateSeparator date={dateVideos[0].scheduled_date} />
            {dateVideos.map((video, videoIndex) => (
              <VideoItem 
                key={videoIndex}
                title={video.title}
                colors={video.colors || video.color}
                temperature={video.temperature}
                postId={video.id}
                comments={video.comments || []}
                stats={video.stats}
                strategy={video.strategy}
              />
            ))}
          </div>
        ))}
    </div>
  );
};

const EngagementGraph = ({ history, color }) => {
  if (!history || history.length === 0) return null;

  const maxValue = Math.max(...history.map(h => h.value));
  const minValue = Math.min(...history.map(h => h.value));
  const range = maxValue - minValue;
  
  // Calculate points for the SVG path
  const points = history.map((point, index) => {
    const x = (index / (history.length - 1)) * 100;
    const y = 100 - ((point.value - minValue) / range) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="pt-2">
      <div className="h-24 w-full relative">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Background grid lines */}
          <line x1="0" y1="25" x2="100" y2="25" stroke="#f0f0f0" strokeWidth="0.5" />
          <line x1="0" y1="50" x2="100" y2="50" stroke="#f0f0f0" strokeWidth="0.5" />
          <line x1="0" y1="75" x2="100" y2="75" stroke="#f0f0f0" strokeWidth="0.5" />
          
          {/* Line graph */}
          <polyline
            points={points}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[10px] text-gray-400 pointer-events-none">
          <span>{Math.round(maxValue * 10) / 10}%</span>
          <span>{Math.round(minValue * 10) / 10}%</span>
        </div>
        
        {/* X-axis labels */}
        <div className="absolute bottom-0 w-full flex justify-between text-[10px] text-gray-400 pointer-events-none">
          <span>{new Date(history[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          <span>{new Date(history[history.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        </div>
      </div>
    </div>
  );
};

// Add keyframes for task completion animation
const taskCompletionAnimation = `
  @keyframes taskComplete {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }
  .task-complete {
    animation: taskComplete 0.5s ease-out;
  }
`;

const TaskDetails = ({ task, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div 
        ref={modalRef}
        className="bg-[#151A21] border border-gray-800 rounded-2xl w-full max-w-lg p-6 m-4 shadow-xl animate-fade-in-up"
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-100">{task.title}</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Progress section */}
          <div className="p-4 rounded-lg bg-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Progress</span>
              <span className="text-sm font-medium text-gray-300">{task.complete}/{task.goal}</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-300"
                style={{ width: `${(task.complete / task.goal) * 100}%` }}
              />
            </div>
          </div>

          {/* Query details if available */}
          {task.query && (
            <div className="p-4 rounded-lg bg-gray-800/50">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Query Details</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Type:</span>
                  <span className="text-sm text-gray-300">{task.query.type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Value:</span>
                  <span className="text-sm text-gray-300">{task.query.value}</span>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-3 pt-4">
            <button
              className="flex-1 px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900"
              onClick={() => {
                // Handle task completion
                onClose();
              }}
            >
              Complete Task
            </button>
            <button
              className="px-4 py-2 rounded-lg border border-gray-700 hover:border-gray-600 text-gray-300 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TaskList = ({ tasks }) => {
  if (!tasks || tasks.length === 0) return null;

  const [selectedTask, setSelectedTask] = useState(null);
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [hoveredTask, setHoveredTask] = useState(null);

  const getTaskStatus = (task) => {
    const percentage = (task.complete / task.goal) * 100;
    if (percentage === 100) return 'completed';
    if (percentage >= 50) return 'in-progress';
    if (percentage > 0) return 'started';
    return 'pending';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'from-green-500/20 to-green-500/10';
      case 'in-progress': return 'from-yellow-500/20 to-yellow-500/10';
      case 'started': return 'from-blue-500/20 to-blue-500/10';
      default: return 'from-gray-500/20 to-gray-500/10';
    }
  };

  useEffect(() => {
    tasks.forEach(task => {
      if (getTaskStatus(task) === 'completed') {
        setCompletedTasks(prev => new Set([...prev, task.id]));
      }
    });
  }, [tasks]);

  return (
    <>
      <style>{taskCompletionAnimation}</style>
      <div className="space-y-3 my-2">
        {tasks.map(task => {
          const percentage = (task.complete / task.goal) * 100;
          const status = getTaskStatus(task);
          const isCompleted = completedTasks.has(task.id);
          const isHovered = hoveredTask === task.id;

          return (
            <div 
              key={task.id} 
              className={cn(
                "rounded-2xl border border-gray-800",
                "bg-gradient-to-br from-gray-900/90 to-gray-800/90 p-4",
                "transition-all transform cursor-pointer backdrop-blur-sm",
                isHovered ? "scale-[1.02] border-gray-700 shadow-lg shadow-black/20" : "hover:scale-[1.02] hover:border-gray-700",
                isCompleted && "task-complete"
              )}
              onMouseEnter={() => setHoveredTask(task.id)}
              onMouseLeave={() => setHoveredTask(null)}
              onClick={() => setSelectedTask(task)}
            >
              <div className="flex items-start gap-3">
                {task.query?.type ? (
                  <div className={cn(
                    "p-2 rounded-lg shrink-0 transition-colors",
                    "bg-gradient-to-br",
                    isHovered ? "from-indigo-500/30 to-purple-500/30" : "from-indigo-500/20 to-purple-500/20"
                  )}>
                    <div className="w-4 h-4 text-indigo-400 font-medium">
                      {task.query.type === 'hashtag' ? '#' : '@'}
                    </div>
                  </div>
                ) : (
                  <div className={cn(
                    "p-2 rounded-lg shrink-0 transition-colors",
                    "bg-gradient-to-br",
                    getStatusColor(status)
                  )}>
                    <div className={cn(
                      "w-2 h-2 rounded-full transition-colors duration-300",
                      status === 'completed' ? "bg-green-500" :
                      status === 'in-progress' ? "bg-yellow-500" :
                      status === 'started' ? "bg-blue-500" :
                      "bg-gray-500"
                    )} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm text-gray-200 font-medium truncate">{task.title}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-400 tabular-nums shrink-0">
                        {task.complete}/{task.goal}
                      </span>
                      {status === 'completed' && (
                        <div className={cn(
                          "w-4 h-4 rounded-full flex items-center justify-center",
                          "bg-gradient-to-br",
                          "from-green-500/30 to-emerald-500/30"
                        )}>
                          <svg 
                            className="w-2.5 h-2.5 text-green-500" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M5 13l4 4L19 7" 
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className={cn(
                          "h-full rounded-full transition-all duration-300",
                          status === 'completed' 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                            : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                        )}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  {task.query?.value && (
                    <div className="mt-2">
                      <span className="text-xs text-gray-500">{task.query.value}</span>
                    </div>
                  )}
                  {status === 'in-progress' && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <div className="w-1 h-1 rounded-full bg-yellow-500 animate-pulse" />
                      <span className="text-xs text-yellow-500">In progress</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Task details modal */}
      {selectedTask && (
        <TaskDetails
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </>
  );
};

const OplaeCard = ({ oplae, isInactive }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const circleColor = isInactive ? '#9CA3AF' : oplae.color;
  const cardColor = isInactive ? '#151A21' : '#151A21';
  const titleColor = isInactive ? 'text-gray-400' : 'text-gray-100';

  // Calculate videos for the current cadence period
  const videosThisCadence = useMemo(() => {
    if (!oplae.goal?.cadence || !oplae.videos) return [];

    const now = new Date();
    let startDate;

    // Set the start date based on cadence
    switch (oplae.goal.cadence.toLowerCase()) {
      case 'daily':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'weekly':
        // Get the start of the current week (Sunday)
        startDate = new Date(now);
        startDate.setDate(now.getDate() - now.getDay());
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'monthly':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'yearly':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return [];
    }

    // Filter videos posted within the current cadence period
    return oplae.videos.filter(video => {
      const postedAt = new Date(video.posted_at);
      return postedAt >= startDate && postedAt <= now;
    });
  }, [oplae.goal?.cadence, oplae.videos]);

  const progressPercent = oplae.goal ? (videosThisCadence.length / oplae.goal.videos_count) * 100 : 0;

  const getDistributionLabel = () => {
    if (isInactive) return 'Completed';
    if (!oplae.goal?.cadence) return null;
    return oplae.goal.cadence.charAt(0).toUpperCase() + oplae.goal.cadence.slice(1);
  };

  const getBadgeColors = () => {
    if (isInactive) {
      return 'bg-gray-800/50 text-gray-400';
    }
    return 'bg-green-900/50 text-green-400';
  };

  return (
    <div className="rounded-2xl p-6 border border-gray-800 hover:bg-opacity-90 transition-all transform hover:scale-[1.02] cursor-pointer bg-[#151A21]">
      <div className="flex items-start mb-4">
        <div 
          className="w-2.5 h-2.5 rounded-full mr-2 shrink-0 mt-1.5 ring-1 ring-gray-800"
          style={{ backgroundColor: circleColor }}
        />
        <div className="flex-1">
          <h2 className={`text-lg font-semibold ${titleColor} leading-tight`}>
            {oplae.name}
          </h2>
          <div className="flex flex-col gap-1 mt-1">
            {oplae.strategy && (
              <div className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${getBadgeColors()} self-start leading-none`}>
                {oplae.strategy}
              </div>
            )}
            {getDistributionLabel() && (
              <p className="text-sm text-gray-400">
                {getDistributionLabel()}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-auto space-y-2">
        {oplae.goal?.cadence && (
          <div className="flex items-center gap-1.5">
            <div className="flex-1 bg-gray-800 rounded-full h-2 relative">
              <div 
                className="h-2 rounded-full transition-all duration-300" 
                style={{ 
                  backgroundColor: circleColor,
                  width: `${progressPercent}%` 
                }}
              />
              {videosThisCadence.length === oplae.goal.videos_count && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg 
                    className="w-3 h-3 text-gray-100" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={3} 
                      d="M5 13l4 4L19 7" 
                    />
                  </svg>
                </div>
              )}
            </div>
            <span className="text-sm text-gray-400 whitespace-nowrap shrink-0 tabular-nums">
              {videosThisCadence.length}/{oplae.goal.videos_count}
            </span>
          </div>
        )}
        {oplae.engagement_rate > 0 && (
          <>
            <div className="border-t border-gray-800 my-3" />
            <div 
              className="flex items-center justify-between cursor-pointer hover:bg-gray-800/50 -mx-2 px-2 py-1 rounded transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              <span className="text-sm text-gray-400">Engagement rate</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-300">{oplae.engagement_rate}%</span>
                {oplae.engagement_rate_history?.length > 0 && (
                  isExpanded ? 
                    <ChevronUp className="w-4 h-4 text-gray-400" /> :
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </div>
            {isExpanded && oplae.engagement_rate_history?.length > 0 && (
              <EngagementGraph history={oplae.engagement_rate_history} color={circleColor} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Add keyframes for fade-in-up animation
const fadeInUpAnimation = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(1rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fade-in-up {
    animation: fadeInUp 0.2s ease-out;
  }
`;

const InfluenceScore = ({ score = 0, level = "Rising Star", metrics = [], nextLevel = null }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  // Example data from DashboardPage
  const audienceInsights = {
    growth: {
      total: "45.2K",
      change: "+15.8%",
      breakdown: [
        { platform: "instagram", share: 60 },
        { platform: "tiktok", share: 40 }
      ]
    }
  };

  const contentInsights = {
    topPerforming: [
      {
        title: "Most Viewed",
        stats: "1.2M views",
        platform: "instagram",
        content: "How to Master Social Media Marketing in 2024"
      },
      {
        title: "Most Engaged",
        stats: "85.5K engagements",
        platform: "tiktok",
        content: "Digital Marketing Trends for 2024"
      }
    ]
  };

  const actionableInsights = [
    {
      insight: "Video Length Impact",
      recommendation: "Videos between 45-60s have 2.3x higher engagement",
      action: "Optimize upcoming content duration"
    },
    {
      insight: "Peak Posting Times",
      recommendation: "Engagement spikes between 6-8 PM EST",
      action: "Schedule next posts within this window"
    }
  ];

  const handleShareStats = async () => {
    const statsUrl = `${window.location.origin}/stats`;
    try {
      await navigator.clipboard.writeText(statsUrl);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <>
      {showCopiedToast && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg text-sm animate-fade-in-up">
          Stats link copied to clipboard!
        </div>
      )}
      <div className="relative bg-[#151A21] border border-gray-800 rounded-2xl overflow-hidden transition-all duration-300">
        {/* Main score section - make it clickable */}
        <div 
          className="relative z-10 p-6 cursor-pointer hover:bg-gray-800/30 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex flex-col space-y-6">
            {/* Top row with score and metrics */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                {/* Score and level */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-indigo-500" />
                    <span className="text-3xl font-bold text-gray-100">{score}</span>
                  </div>
                  
                  <div className="h-8 w-px bg-gray-800" />

                  {/* Metrics */}
                  <div className="flex items-center gap-8">
                    {metrics.map((metric, index) => (
                      <div key={index} className="flex items-center gap-6">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            <span className="text-lg font-semibold text-gray-100">{metric.value}</span>
                            {metric.trend === 'up' && (
                              <TrendingUp className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                          <span className="text-xs text-gray-400">{metric.name}</span>
                        </div>
                        {index < metrics.length - 1 && (
                          <div className="h-8 w-px bg-gray-800" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Next level info and expand button */}
                <div className="flex items-center gap-3">
                  {nextLevel && (
                    <>
                      <div className="h-8 w-px bg-gray-800" />
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-400">Next: {nextLevel.name}</span>
                        <span className="text-sm text-gray-400">({nextLevel.remaining})</span>
                      </div>
                    </>
                  )}
                  <div className="h-8 w-px bg-gray-800" />
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            {nextLevel && (
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-300"
                  style={{ width: `${nextLevel.progress}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Expandable stats section */}
        <div 
          className={cn(
            "border-t border-gray-800 transition-all duration-300 ease-in-out",
            isExpanded ? "opacity-100 max-h-[2000px]" : "opacity-0 max-h-0 overflow-hidden"
          )}
        >
          <div className="p-4 space-y-6">
            {/* Share Stats Button */}
            <div className="flex justify-end">
              <button
                onClick={handleShareStats}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 text-sm font-medium transition-colors"
              >
                <Share2 className="w-4 h-4 mr-1.5" />
                Share Stats
              </button>
            </div>

            {/* Growth Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-500" />
                <h3 className="text-base font-medium text-gray-100">Audience Growth</h3>
              </div>
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl font-bold text-gray-100">
                      {audienceInsights.growth.total}
                    </div>
                    <div className="px-2 py-0.5 rounded-full bg-green-900/30 text-green-400 text-sm font-medium">
                      {audienceInsights.growth.change}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {audienceInsights.growth.breakdown.map((platform, index) => (
                      <div key={index} className="flex items-center gap-3">
                        {platform.platform === 'instagram' ? (
                          <Instagram className="w-3.5 h-3.5 text-pink-500" />
                        ) : (
                          <TiktokIcon className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                            style={{ width: `${platform.share}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 tabular-nums w-8 text-right">{platform.share}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Performing Content */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-500" />
                <h3 className="text-base font-medium text-gray-100">Top Performing Content</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {contentInsights.topPerforming.map((content, index) => (
                  <div 
                    key={index}
                    className="p-3 rounded-lg bg-gray-800/50 space-y-1.5"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-100 text-sm">{content.title}</h4>
                      <div className="flex items-center gap-1.5">
                        {content.platform === 'instagram' ? (
                          <Instagram className="w-3.5 h-3.5 text-pink-500" />
                        ) : (
                          <TiktokIcon className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        <span className="text-xs text-gray-400">{content.stats}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-1">{content.content}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Growth Opportunities */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-indigo-500" />
                <h3 className="text-base font-medium text-gray-100">Growth Opportunities</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {actionableInsights.map((insight, index) => (
                  <div 
                    key={index} 
                    className="p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800/70 transition-colors space-y-1.5"
                  >
                    <h4 className="font-medium text-gray-100 text-sm">{insight.insight}</h4>
                    <p className="text-xs text-gray-400">{insight.recommendation}</p>
                    <div className="flex items-center text-indigo-400 text-xs font-medium">
                      <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" />
                      {insight.action}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CrosspostOplae = ({ oplae }) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const platforms = [
    { id: 'youtube', name: 'YouTube', color: '#FF0000' },
    { id: 'instagram', name: 'Instagram', color: '#E4405F' },
    { id: 'twitter', name: 'Twitter', color: '#1DA1F2' },
    { id: 'tiktok', name: 'TikTok', color: '#000000' }
  ];

  const handlePlatformToggle = (platformId) => {
    setSelectedPlatforms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(platformId)) {
        newSet.delete(platformId);
      } else {
        newSet.add(platformId);
      }
      return newSet;
    });
  };

  return (
    <div>
      <style>{fadeInUpAnimation}</style>
      <div className="space-y-4">
        <div className="rounded-2xl p-6 border border-gray-800 hover:bg-opacity-90 transition-all transform hover:scale-[1.02] cursor-pointer bg-[#151A21]">
          <div className="flex items-start mb-4">
            <div 
              className="w-2.5 h-2.5 rounded-full mr-2 shrink-0 mt-1.5 ring-1 ring-gray-800"
              style={{ backgroundColor: oplae.color || '#3B82F6' }}
            />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-100 leading-tight">
                {oplae.title}
              </h2>
              <div className="flex flex-col gap-1 mt-1">
                <div className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-blue-900/50 text-blue-400 self-start leading-none">
                  Crosspost
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              {platforms.map(platform => (
                <button
                  key={platform.id}
                  onClick={() => handlePlatformToggle(platform.id)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-sm font-medium transition-all",
                    "border border-gray-700 hover:border-gray-600",
                    selectedPlatforms.has(platform.id)
                      ? "bg-gray-800 text-gray-100"
                      : "bg-transparent text-gray-400"
                  )}
                >
                  <div className="flex items-center space-x-1.5">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: platform.color }}
                    />
                    <span>{platform.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-b-lg overflow-hidden">
          <VideoList videos={oplae.videos} />
        </div>
      </div>
    </div>
  );
};

const Oplae = ({ oplae, isInactive = false, showOnlyInfluenceScore = false, hideInfluenceScore = false }) => {
  // Add example influence metrics (you should replace these with real data from your backend)
  const influenceMetrics = {
    score: 78,
    level: "Rising Star",
    metrics: [
      { name: "Engagement", value: "8.2%", trend: "up" },
      { name: "Reach", value: "142K", trend: "up" },
      { name: "Growth", value: "+15%", trend: "up" }
    ],
    nextLevel: {
      name: "Influencer",
      progress: 78,
      remaining: "2.2K followers"
    }
  };

  // If this is a crosspost oplae, render the CrosspostOplae component
  if (oplae.type === 'crosspost') {
    return <CrosspostOplae oplae={oplae} />;
  }

  // If we only want to show the influence score
  if (showOnlyInfluenceScore) {
    return (
      <InfluenceScore 
        score={influenceMetrics.score}
        level={influenceMetrics.level}
        metrics={influenceMetrics.metrics}
        nextLevel={influenceMetrics.nextLevel}
      />
    );
  }

  // Otherwise render the standard Oplae
  return (
    <div className={cn(isInactive ? 'opacity-60' : '', 'pb-8')}>
      <style>{fadeInUpAnimation}</style>
      <div className="space-y-4">
        {!hideInfluenceScore && (
          <InfluenceScore 
            score={influenceMetrics.score}
            level={influenceMetrics.level}
            metrics={influenceMetrics.metrics}
            nextLevel={influenceMetrics.nextLevel}
          />
        )}
        <OplaeCard 
          oplae={oplae}
          isInactive={!oplae.active}
        />
        {oplae.tasks && oplae.tasks.length > 0 && (
          <TaskList tasks={oplae.tasks} />
        )}
        <div className="rounded-b-lg overflow-hidden">
          <VideoList videos={oplae.videos} />
        </div>
      </div>
    </div>
  );
};

export default Oplae; 